import type { Scope, User } from "@sentry/react";

export type SentryModule = typeof import("@sentry/react");
export type CaptureExceptionArgs = Parameters<SentryModule["captureException"]>;
export type CaptureMessageArgs = Parameters<SentryModule["captureMessage"]>;
export type SentryClient = Pick<
  SentryModule,
  "captureException" | "captureMessage" | "setContext" | "setUser" | "withScope"
>;
type ScopeLevel = Parameters<Scope["setLevel"]>[0];
type ScopeContext = Parameters<Scope["setContext"]>[1];
type ScopeExtra = Parameters<Scope["setExtra"]>[1];

interface ScopeSnapshot {
  contexts: Record<string, ScopeContext>;
  extras: Record<string, ScopeExtra>;
  fingerprint?: string[];
  level?: ScopeLevel;
  tags: Record<string, string>;
}

type PendingCapture =
  | { args: CaptureExceptionArgs; kind: "exception"; scope?: ScopeSnapshot }
  | { args: CaptureMessageArgs; kind: "message"; scope?: ScopeSnapshot };

const MAX_PENDING_CAPTURES = 50;

let sentry: SentryClient | null = null;
let pendingCaptures: PendingCapture[] = [];
let currentScope: DeferredScope | null = null;
let currentUser: User | null = null;
const globalContexts = new Map<string, ScopeContext>();

export class DeferredScope {
  readonly snapshot: ScopeSnapshot = {
    contexts: {},
    extras: {},
    tags: {},
  };

  setContext(name: string, context: ScopeContext): this {
    this.snapshot.contexts[name] = context;
    return this;
  }

  setExtra(name: string, extra: ScopeExtra): this {
    this.snapshot.extras[name] = extra;
    return this;
  }

  setFingerprint(fingerprint: string[]): this {
    this.snapshot.fingerprint = fingerprint;
    return this;
  }

  setLevel(level: ScopeLevel): this {
    this.snapshot.level = level;
    return this;
  }

  setTag(name: string, value: string): this {
    this.snapshot.tags[name] = value;
    return this;
  }
}

function cloneScope(scope: DeferredScope | null): ScopeSnapshot | undefined {
  if (!scope) return undefined;
  return {
    contexts: { ...scope.snapshot.contexts },
    extras: { ...scope.snapshot.extras },
    fingerprint: scope.snapshot.fingerprint
      ? [...scope.snapshot.fingerprint]
      : undefined,
    level: scope.snapshot.level,
    tags: { ...scope.snapshot.tags },
  };
}

function applyScope(scope: Scope, snapshot: ScopeSnapshot | undefined): void {
  if (!snapshot) return;
  if (snapshot.level) scope.setLevel(snapshot.level);
  if (snapshot.fingerprint) scope.setFingerprint(snapshot.fingerprint);
  for (const [name, value] of Object.entries(snapshot.tags)) {
    scope.setTag(name, value);
  }
  for (const [name, value] of Object.entries(snapshot.contexts)) {
    scope.setContext(name, value);
  }
  for (const [name, value] of Object.entries(snapshot.extras)) {
    scope.setExtra(name, value);
  }
}

function sendCapture(sdk: SentryClient, capture: PendingCapture): string {
  let eventId = "";
  sdk.withScope((scope) => {
    applyScope(scope, capture.scope);
    eventId =
      capture.kind === "exception"
        ? sdk.captureException(...capture.args)
        : sdk.captureMessage(...capture.args);
  });
  return eventId;
}

function queueCapture(capture: PendingCapture): string {
  if (sentry) return sendCapture(sentry, capture);
  if (pendingCaptures.length === MAX_PENDING_CAPTURES) pendingCaptures.shift();
  pendingCaptures.push(capture);
  return "";
}

export function connectSentry(sdk: SentryClient): void {
  sentry = sdk;
  sdk.setUser(currentUser);
  for (const [name, context] of globalContexts) {
    sdk.setContext(name, context);
  }

  const captures = pendingCaptures;
  pendingCaptures = [];
  for (const capture of captures) sendCapture(sdk, capture);
}

export function captureException(...args: CaptureExceptionArgs): string {
  return queueCapture({
    args,
    kind: "exception",
    scope: cloneScope(currentScope),
  });
}

export function captureMessage(...args: CaptureMessageArgs): string {
  return queueCapture({
    args,
    kind: "message",
    scope: cloneScope(currentScope),
  });
}

export function setContext(name: string, context: ScopeContext): void {
  globalContexts.set(name, context);
  sentry?.setContext(name, context);
}

export function setUser(user: User | null): void {
  currentUser = user;
  sentry?.setUser(user);
}

export function withScope<T>(callback: (scope: DeferredScope) => T): T {
  const previousScope = currentScope;
  const scope = new DeferredScope();
  if (previousScope) {
    Object.assign(scope.snapshot.contexts, previousScope.snapshot.contexts);
    Object.assign(scope.snapshot.extras, previousScope.snapshot.extras);
    Object.assign(scope.snapshot.tags, previousScope.snapshot.tags);
    scope.snapshot.fingerprint = previousScope.snapshot.fingerprint
      ? [...previousScope.snapshot.fingerprint]
      : undefined;
    scope.snapshot.level = previousScope.snapshot.level;
  }
  currentScope = scope;
  try {
    return callback(scope);
  } finally {
    currentScope = previousScope;
  }
}
